import { presenterBlocage } from '../dossier/blocages.js';
import { REFERENTIEL_PILOTAGE } from './referentiel.js';

/** Modèle de D01 (tableau de bord) et M01 (décisions) : types lus du contrat et règles d'affichage. */
export interface Argent {
  amountCents: number;
  currency: 'EUR';
}
export interface StatutMetier {
  code: string;
  libelle: string;
}
export interface LignePilotage {
  id: string;
  reference: string;
  title: string;
  status: 'prospect' | 'actif' | 'bloque' | 'clos' | 'abandonne';
  currentPhase: string;
  blocker: { kind: string; label: string; since: string } | null;
  estimatedValue: { low: Argent; high: Argent } | null;
  etudeName: string | null;
  heirsCount: number;
  respondedCount: number;
  toFollowUpCount: number;
  unreachableCount: number;
  /** Absent tant que le contrat ne le porte pas (RFC-L51-001) : jamais déduit. */
  statutMetier?: StatutMetier | null | undefined;
  typeDossier?: StatutMetier | null | undefined;
}
export interface TableauPilotage {
  dossiers: LignePilotage[];
  pendingDecisions: number;
  collected: Argent;
  outstanding: Argent;
  teamActivity: { userDisplayName: string; actionsToday: number }[];
  /** Statuts du référentiel servis par le serveur (RFC-L51-001), zéro compris ; absent d'un serveur plus ancien. */
  statuts?: CompteStatut[] | undefined;
}
export interface Decision {
  id: string;
  dossierId: string;
  question: string;
  options: { code: string; label: string; costEstimate: Argent | null }[];
  reservedToRole: 'dirigeante' | 'admin';
  decidedOption: string | null;
  decidedAt: string | null;
}
export interface Echeance {
  id: string;
  dossierId: string;
  kind: string;
  label: string;
  dueAt: string;
  status: 'a_venir' | 'proche' | 'depassee' | 'faite';
}
export interface RelanceAValider {
  id: string;
  dossierId: string;
  personId: string;
  renderedBody: string;
  scheduledAt: string | null;
  state: string;
}
export interface DonneesPilotage {
  tableau: TableauPilotage;
  decisions: Decision[];
  echeances: Echeance[];
  relances: RelanceAValider[];
  relancesIndisponible: string | null;
}

/** Mention obligatoire de toute estimation affichée (règle non négociable, réservée à l'équipe). */
export const MENTION_ESTIMATION = 'Estimation indicative, ne vaut pas évaluation';

const EST_ACTIF = (l: LignePilotage): boolean => l.status !== 'clos' && l.status !== 'abandonne';
const NBSP = '\u00a0';

/**
 * Du plus bloqué au plus avancé (CA-ISA-01a) : les dossiers bloqués d'abord, le blocage le plus ancien en tête, puis la
 * valeur estimée haute décroissante (valeur absente en dernier), puis la référence. Même ordre que le serveur.
 */
export function trierDossiers(lignes: readonly LignePilotage[]): LignePilotage[] {
  return [...lignes].sort(
    (a, b) =>
      Number(b.blocker !== null) - Number(a.blocker !== null) ||
      (a.blocker?.since ?? '').localeCompare(b.blocker?.since ?? '') ||
      (b.estimatedValue?.high.amountCents ?? -1) - (a.estimatedValue?.high.amountCents ?? -1) ||
      a.reference.localeCompare(b.reference),
  );
}

const jourUtc = (date: string): number => {
  const [a = 0, m = 1, j = 1] = date.slice(0, 10).split('-').map(Number);
  return Date.UTC(a, m - 1, j);
};

/** Nombre de jours entiers écoulés depuis `since` ; jamais négatif. */
export function ancienneteJours(since: string, aujourdhui: string): number {
  return Math.max(0, Math.round((jourUtc(aujourdhui) - jourUtc(since)) / 86_400_000));
}

export interface Indicateurs {
  actifs: number;
  bloques: number;
  parNature: { nature: string; libelle: string; nombre: number }[];
}

/** Libellé d'un blocage : la table versionnée de la fiche dossier d'abord, puis les natures du pilotage, jamais un code brut. */
function libelleNature(nature: string): string {
  const versionne = presenterBlocage({ kind: nature, label: nature }).libelle;
  const generique = presenterBlocage({ kind: 'code_inconnu', label: 'code_inconnu' }).libelle;
  return versionne !== generique ? versionne : (REFERENTIEL_PILOTAGE.natures[nature] ?? generique);
}

export function indicateurs(lignes: readonly LignePilotage[]): Indicateurs {
  const actifs = lignes.filter(EST_ACTIF);
  const bloques = actifs.filter((l) => l.blocker !== null);
  const parNature = new Map<string, number>();
  for (const l of bloques) {
    const nature = l.blocker?.kind ?? '';
    parNature.set(nature, (parNature.get(nature) ?? 0) + 1);
  }
  return {
    actifs: actifs.length,
    bloques: bloques.length,
    parNature: [...parNature.entries()]
      .map(([nature, nombre]) => ({ nature, libelle: libelleNature(nature), nombre }))
      .sort((a, b) => b.nombre - a.nombre || a.libelle.localeCompare(b.libelle, 'fr')),
  };
}

export interface CompteStatut {
  code: string;
  libelle: string;
  nombre: number;
}

/**
 * Les neuf statuts métier, dans l'ordre du référentiel, zéro compris. Servis par le serveur (`statuts` du tableau de bord) : ils
 * font foi, même sans aucun dossier. À défaut, comptés sur les lignes ; `disponible` est faux si aucune ne porte de statut.
 */
export function compterParStatut(
  lignes: readonly LignePilotage[],
  servis?: readonly CompteStatut[],
): { statuts: CompteStatut[]; disponible: boolean } {
  if (servis && servis.length > 0) return { disponible: true, statuts: servis.map((s) => ({ ...s })) };
  const porteurs = lignes.filter((l) => l.statutMetier);
  return {
    disponible: porteurs.length > 0,
    statuts: REFERENTIEL_PILOTAGE.statuts.map(({ code, libelle }) => {
      const dossiers = porteurs.filter((l) => l.statutMetier?.code === code);
      return { code, libelle: dossiers[0]?.statutMetier?.libelle ?? libelle, nombre: dossiers.length };
    }),
  };
}

export function filtrerParStatut(lignes: readonly LignePilotage[], code: string | null): LignePilotage[] {
  return code === null ? [...lignes] : lignes.filter((l) => l.statutMetier?.code === code);
}

/** Comptes rendus à faire (#31, RG-26) : dossiers actifs au statut STA-09. */
export function comptesRendusARedigier(lignes: readonly LignePilotage[]): LignePilotage[] {
  return lignes.filter((l) => EST_ACTIF(l) && l.statutMetier?.code === REFERENTIEL_PILOTAGE.codeCompteRendu);
}

export function formaterEuros(centimes: number): string {
  const absolu = Math.abs(centimes);
  const entier = Math.floor(absolu / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  const reste = absolu % 100;
  const decimales = reste === 0 ? '' : `,${String(reste).padStart(2, '0')}`;
  return `${centimes < 0 ? '-' : ''}${entier}${decimales}${NBSP}€`;
}

/** Estimation de départ d'un dossier (valeur présumée du bien, pas un coût). À afficher avec MENTION_ESTIMATION. */
export function libelleValeur(ligne: LignePilotage): string {
  const v = ligne.estimatedValue;
  if (!v) return 'à estimer';
  if (v.low.amountCents === v.high.amountCents) return `≈ ${formaterEuros(v.high.amountCents)}`;
  return `entre ${formaterEuros(v.low.amountCents)} et ${formaterEuros(v.high.amountCents)}`;
}
